/* Header niceties that live OUTSIDE theme.js (search machinery is sacred):
   rotating search terms, the header's height and live bottom edge, and the
   back-to-top button. */
(() => {
  // The phone header's "Search for '…'" term: slides (no fade, so no frame is below
  // contrast), goes round twice once the page is idle, then rests on the first term
  // (content that moves on its own must stop: WCAG 2.2.2).
  const initRotator = () => {
    const rotator = document.querySelector('[data-search-rotator]');
    if (!rotator) return;
    const terms = Array.from(rotator.children);
    if (terms.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let index = 0;
    let turns = 0;
    let timer = 0;
    const step = () => {
      // Don't churn the DOM while the tab is hidden, or while the bar isn't shown.
      if (document.hidden || !rotator.offsetParent) return;
      const current = terms[index];
      index = (index + 1) % terms.length;
      const next = terms[index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      next.classList.add('is-active');
      // Once it is out of sight above, back below the window (without animation: luxe.css)
      setTimeout(() => current.classList.remove('is-leaving'), 480);
      turns += 1;
      if (turns >= terms.length * 2 && index === 0) clearInterval(timer);
    };
    const start = () => {
      timer = setInterval(step, 2600);
    };
    (window.requestIdleCallback || ((callback) => setTimeout(callback, 1500)))(start, { timeout: 4000 });
  };

  // --header-height (on <html>): the header's own height, for anything pinned
  // under the stuck header. It never changes while scrolling, so pinned
  // elements stay perfectly still and scrolling never restyles the page.
  // --header-bottom (on the header): its live bottom edge, which moves while
  // the announcement bar scrolls away. Only an open menu panel uses that (to
  // stay within the screen), so it is written only while a panel is open, and
  // only on the header. A custom property on <html> restyles every element on
  // the page (about 50ms in Safari); written on each scroll step it froze the
  // product page, whose smooth scrolling runs on the page's own clock. While
  // scrolling, the edge is worked out from the scroll position and sizes
  // measured beforehand, so scrolling never forces a layout.
  const initHeaderBottom = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    const rootStyle = document.documentElement.style;
    const pinned = header.closest('.shopify-section-sticky-header') || header;
    let above = 0;
    let height = 0;
    let lastBottom = null;
    let lastHeight = null;
    let queued = false;

    // What sits above the header until it sticks (the announcement bar), and the
    // header's height: read from layout only at start, on resize and when they change.
    const measure = () => {
      let bottom = 0;
      let sibling = pinned.previousElementSibling;
      while (sibling) {
        if (sibling.classList.contains('shopify-section-group-header-group')) {
          bottom = Math.max(bottom, sibling.getBoundingClientRect().bottom + window.scrollY);
        }
        sibling = sibling.previousElementSibling;
      }
      above = Math.max(0, Math.round(bottom));
      height = Math.round(header.getBoundingClientRect().height);
    };
    const panelOpen = () => header.classList.contains('has-open-panel');
    const sync = () => {
      queued = false;
      if (height !== lastHeight) {
        lastHeight = height;
        rootStyle.setProperty('--header-height', height + 'px');
      }
      if (!panelOpen()) return;
      const bottom = Math.max(0, above - Math.round(window.scrollY)) + height;
      if (bottom !== lastBottom) {
        lastBottom = bottom;
        header.style.setProperty('--header-bottom', bottom + 'px');
      }
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    };
    const remeasure = () => {
      measure();
      queue();
    };
    measure();
    sync();
    window.addEventListener('scroll', () => {
      if (panelOpen()) queue();
    }, { passive: true });
    window.addEventListener('resize', remeasure);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(remeasure);
      observer.observe(header);
      if (pinned.previousElementSibling) observer.observe(pinned.previousElementSibling);
    }
    // A panel opening (header-nav.js sets has-open-panel): place the edge before it paints.
    new MutationObserver(() => {
      if (panelOpen()) sync();
    }).observe(header, { attributes: true, attributeFilter: ['class'] });
  };

  // Small back-to-top button once the visitor is a couple screens deep. An
  // observer on the page's first pixel (its box stretched up by one and a half
  // screens) says when, without reading the scroll position on every event.
  const initBackToTop = () => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'back-to-top';
    btn.setAttribute('aria-label', 'Back to Top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.9998 15C17.9998 15 13.5809 9.00001 11.9998 9C10.4187 8.99999 5.99985 15 5.99985 15"/></svg>';
    document.body.appendChild(btn);

    if ('IntersectionObserver' in window) {
      const top = document.createElement('div');
      top.setAttribute('aria-hidden', 'true');
      top.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden';
      document.body.prepend(top);
      new IntersectionObserver(([entry]) => {
        btn.classList.toggle('is-visible', !entry.isIntersecting);
      }, { rootMargin: '150% 0px 0px 0px' }).observe(top);
    } else {
      const toggle = () => btn.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5);
      toggle();
      window.addEventListener('scroll', toggle, { passive: true });
    }
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const init = () => {
    initRotator();
    initHeaderBottom();
    initBackToTop();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
